import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Globe, Loader2, CheckCircle2, Clock, Copy, RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  "Enter your domain below (e.g. shankar-lab.com). ARIA registers it with Resend for you.",
  "Add the DNS records shown to your domain's DNS provider (Namecheap, Cloudflare, GoDaddy…). Copy each value exactly.",
  "Wait a few minutes for DNS to propagate, then click Verify. Status turns to Verified.",
  "Pick a sender address on that domain (e.g. aria@yourdomain.com) and save. Nudges can then reach any inbox.",
];

export function SenderDomain() {
  const [status, setStatus] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(null);
  const [sender, setSender] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/nudge/sender"); setStatus(data); setSender(data.sender_email.endsWith("@resend.dev") ? "" : data.sender_email); }
    catch (err) { console.error("[ARIA]", err); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    setBusy("add");
    try { await api.post("/nudge/sender/domains", { name }); setName(""); toast.success("Domain added — now add the DNS records"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add domain", { duration: 6000 }); }
    finally { setBusy(null); }
  };
  const verify = async (id) => {
    setBusy(id);
    try { const { data } = await api.post(`/nudge/sender/domains/${id}/verify`); toast[data.status === "verified" ? "success" : "info"](data.status === "verified" ? "Domain verified" : `Status: ${data.status} — DNS may still be propagating`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
    finally { setBusy(null); }
  };
  const saveSender = async () => {
    setBusy("sender");
    try { const { data } = await api.put("/nudge/sender", { sender_email: sender }); toast.success(`Sending as ${data.sender_email}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not set sender", { duration: 6000 }); }
    finally { setBusy(null); }
  };
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied", { duration: 900 }); };

  if (!status) return null;
  const verified = status.verified.length > 0;

  return (
    <div className="aria-card p-5 mb-6" data-testid="sender-domain">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Globe size={15} className="text-[var(--aria-primary)]" /><span className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold">Sending domain</span></div>
        <span className={`aria-pill text-[10px] ${status.using_test_sender ? "text-amber-400 border-amber-400/40" : "aria-pill-success"}`} data-testid="sender-status-pill">
          {status.using_test_sender ? "Test sender · owner inbox only" : `Sending as ${status.sender_email}`}
        </span>
      </div>
      {status.using_test_sender && (
        <div className="text-xs text-amber-300/90 flex items-start gap-2 mb-3" data-testid="sender-warning">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> Right now ARIA sends from onboarding@resend.dev, so Resend only delivers to the inbox that owns the Resend account. Verify your own domain to send anywhere.
        </div>
      )}
      <ol className="text-sm text-[var(--aria-text-muted)] space-y-1 mb-4 list-decimal pl-5" data-testid="sender-steps">
        {STEPS.map((s, i) => <li key={i}>{s}</li>)}
      </ol>

      <div className="flex gap-2 mb-4">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="yourdomain.com" data-testid="sender-domain-input"
          onKeyDown={e => e.key === "Enter" && add()}
          className="flex-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
        <button onClick={add} disabled={busy === "add" || !name.trim()} data-testid="sender-domain-add" className="btn btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
          {busy === "add" ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />} Add domain
        </button>
        <button onClick={load} data-testid="sender-domain-refresh" className="px-3 rounded-md border border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"><RefreshCw size={14} /></button>
      </div>

      {status.domains.map(d => (
        <div key={d.id} className="rounded-md border border-[var(--aria-border)] p-4 mb-3" data-testid={`sender-domain-${d.name}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {d.status === "verified" ? <CheckCircle2 size={15} className="text-emerald-400" /> : <Clock size={15} className="text-amber-400" />}
              <span className="font-medium text-sm">{d.name}</span>
              <span className="text-xs text-[var(--aria-text-muted)]">· {d.status}{d.region ? ` · ${d.region}` : ""}</span>
            </div>
            {d.status !== "verified" && (
              <button onClick={() => verify(d.id)} disabled={busy === d.id} data-testid={`sender-domain-verify-${d.name}`} className="text-xs text-[var(--aria-primary)] hover:underline inline-flex items-center gap-1">
                {busy === d.id ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />} Verify
              </button>
            )}
          </div>
          {d.records.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono">
                <thead><tr className="text-[var(--aria-text-muted)] text-left"><th className="py-1 pr-3">Type</th><th className="py-1 pr-3">Name / Host</th><th className="py-1 pr-3">Value</th><th className="py-1">Status</th></tr></thead>
                <tbody>
                  {d.records.map((r, i) => (
                    <tr key={i} className="border-t border-[var(--aria-border)] align-top">
                      <td className="py-1.5 pr-3">{r.type}{r.priority != null ? ` (prio ${r.priority})` : ""}</td>
                      <td className="py-1.5 pr-3 break-all"><button onClick={() => copy(r.name)} className="hover:text-[var(--aria-primary)] inline-flex items-center gap-1 text-left">{r.name} <Copy size={10} /></button></td>
                      <td className="py-1.5 pr-3 break-all max-w-[280px]"><button onClick={() => copy(r.value)} className="hover:text-[var(--aria-primary)] inline-flex items-center gap-1 text-left">{r.value} <Copy size={10} /></button></td>
                      <td className={`py-1.5 ${r.status === "verified" ? "text-emerald-400" : "text-amber-400"}`}>{r.status || "pending"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      <div className="flex gap-2 items-end mt-2">
        <div className="flex-1">
          <label className="text-xs text-[var(--aria-text-muted)]">Send from {verified ? `(on ${status.verified.join(", ")})` : "(available once a domain is verified)"}</label>
          <input value={sender} onChange={e => setSender(e.target.value)} placeholder={verified ? `aria@${status.verified[0]}` : "aria@yourdomain.com"} disabled={!verified} data-testid="sender-email-input"
            className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm disabled:opacity-50" />
        </div>
        <button onClick={saveSender} disabled={!verified || busy === "sender"} data-testid="sender-email-save" className="btn btn-primary px-4 py-2 text-sm disabled:opacity-50">Use this sender</button>
      </div>
    </div>
  );
}
